import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { applicationsApi } from "../api/applications";
import { ApiError } from "../api/client";
import {
  APPLICATION_STATUSES,
  ARTIFACT_LABELS,
  type ApplicationStatus,
  type ArtifactKind,
} from "../api/types";
import DocumentPanel from "../components/DocumentPanel";
import JobProgress from "../components/JobProgress";
import { STATUS_LABELS } from "../components/StatusBadge";
import { Alert, Button, Card, Spinner } from "../components/ui";
import { FitScore } from "./Applications";

// The four documents worth reading, in the order they are produced.
const TABS: ArtifactKind[] = [
  "resume_md",
  "cover_letter_md",
  "fitness_report",
  "job_ad_notes",
];

export default function ApplicationDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ArtifactKind>("resume_md");
  const [jobId, setJobId] = useState<string | null>(null);

  const key = ["application", id];
  const { data, isPending, error } = useQuery({
    queryKey: key,
    queryFn: () => applicationsApi.read(id),
  });

  const generate = useMutation({
    mutationFn: (kinds?: ArtifactKind[]) => applicationsApi.generate(id, kinds),
    onSuccess: ({ job_id }) => setJobId(job_id),
  });

  const update = useMutation({
    mutationFn: (status: ApplicationStatus) => applicationsApi.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const remove = useMutation({
    mutationFn: () => applicationsApi.remove(id),
    onSuccess: () => navigate("/"),
  });

  if (isPending) return <Spinner label="Loading application…" />;
  if (error || !data) return <Alert kind="error">This application could not be loaded.</Alert>;

  const activeJob = jobId ?? data.active_job_id;
  const artifact = data.artifacts.find((a) => a.kind === tab);
  const hasDocuments = data.artifacts.some((a) => !a.kind.endsWith("_pdf"));
  const generateError = generate.error instanceof ApiError ? generate.error.message : null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/" className="text-sm text-gray-500 hover:underline">
            ← All applications
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-gray-900">
            {data.role || "Reading the job ad…"}
          </h1>
          <p className="mt-0.5 text-sm text-gray-600">
            {[data.company, data.location, data.employment_type].filter(Boolean).join(" · ")}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {data.fit_score !== null && <FitScore score={data.fit_score} />}
          <select
            value={data.status}
            onChange={(e) => update.mutate(e.target.value as ApplicationStatus)}
            className="rounded-md border border-gray-300 bg-surface px-3 py-1.5 text-sm"
          >
            {APPLICATION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      </header>

      {generateError && <Alert kind="error">{generateError}</Alert>}

      {activeJob && (
        <JobProgress
          jobId={activeJob}
          onFinished={() => {
            setJobId(null);
            queryClient.invalidateQueries({ queryKey: key });
            queryClient.invalidateQueries({ queryKey: ["applications"] });
          }}
        />
      )}

      {!hasDocuments && !activeJob && (
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-600">Nothing has been generated for this job yet.</p>
            <Button
              className="mt-3"
              onClick={() => generate.mutate(undefined)}
              disabled={generate.isPending}
            >
              Generate documents
            </Button>
          </div>
        </Card>
      )}

      {hasDocuments && (
        <Card>
          <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200 pb-3">
            {TABS.map((kind) => {
              const present = data.artifacts.find((a) => a.kind === kind);
              return (
                <button
                  key={kind}
                  type="button"
                  disabled={!present}
                  onClick={() => setTab(kind)}
                  className={
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                    (tab === kind
                      ? "bg-accent-50 text-accent-700"
                      : present
                        ? "text-gray-600 hover:bg-gray-100"
                        : "cursor-not-allowed text-gray-300")
                  }
                >
                  {ARTIFACT_LABELS[kind]}
                  {present?.state === "stale" && " ·"}
                </button>
              );
            })}

            <Button
              variant="secondary"
              className="ms-auto"
              onClick={() => generate.mutate(undefined)}
              disabled={Boolean(activeJob) || generate.isPending}
            >
              Regenerate everything
            </Button>
          </div>

          {artifact ? (
            <DocumentPanel
              applicationId={id}
              artifact={artifact}
              artifacts={data.artifacts}
              busy={Boolean(activeJob)}
              onRegenerate={(kinds) => generate.mutate(kinds)}
            />
          ) : (
            <p className="text-sm text-gray-500">This document has not been generated yet.</p>
          )}
        </Card>
      )}

      <details className="rounded-lg border border-gray-200 bg-surface">
        <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-gray-800">
          The job ad
        </summary>
        <pre className="max-h-96 overflow-auto border-t border-gray-200 p-5 text-xs whitespace-pre-wrap text-gray-700">
          {data.job_ad_md}
        </pre>
      </details>

      <div className="pt-2">
        <button
          type="button"
          onClick={() => {
            if (confirm("Delete this application and everything generated for it?")) {
              remove.mutate();
            }
          }}
          className="text-sm text-red-600 hover:underline"
        >
          Delete this application
        </button>
      </div>
    </div>
  );
}
